// ═══════════════════════════════════════════════════════════════════
// COTIZADOR — Fase 1: Datos del viaje
// ═══════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { ORIGENES, DESTINOS_COTIZADOR, ORIGEN_DEFAULT, FDE, COSTO_OP_POR_PERSONA, MARGEN_DEFAULT } from "../config/cotizador"
import { AEROLINEAS, HOTELES } from "../components/admin/propuestasData"
import {
  calcNights, addNights, fmtDDMM,
  buildOlaUrl, buildPNUrl,
  getDestinoInfo, destinoRequiereAeropuerto,
  getDestinosCompatibles,
  calcTransfers, calcCosts,
} from "../utils/cotizadorUtils"

// ─── Estilos inline (dark theme, consistente con admin/generador) ────
const S = {
  page:        { maxWidth:880, margin:"0 auto", padding:"32px 20px", fontFamily:"Inter, sans-serif", color:"#e5e7eb" },
  titulo:      { fontSize:"1.5rem", fontWeight:800, color:"#f9fafb", marginBottom:4 },
  sub:         { fontSize:"0.875rem", color:"#6b7280", marginBottom:32 },
  card:        { background:"#111", border:"1px solid #2a2a2a", borderRadius:12, padding:"20px 24px", marginBottom:16 },
  cardTit:     { fontSize:"0.68rem", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em", color:"#c9a84c", marginBottom:16 },
  lbl:         { display:"block", fontSize:"0.68rem", fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 },
  input:       { width:"100%", padding:"10px 14px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:8, color:"#e5e7eb", fontFamily:"Inter, sans-serif", fontSize:"0.875rem", outline:"none", boxSizing:"border-box" },
  select:      { width:"100%", padding:"10px 14px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:8, color:"#e5e7eb", fontFamily:"Inter, sans-serif", fontSize:"0.875rem", outline:"none", cursor:"pointer", boxSizing:"border-box" },
  twoCol:      { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 },
  threeCol:    { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 },
  field:       { marginBottom:12 },
  divider:     { height:1, background:"#1f1f1f", margin:"16px 0" },
  // Botón toggle (1 / 2 destinos)
  toggleWrap:  { display:"flex", gap:8, marginBottom:16 },
  toggleBtn:   a => ({ padding:"9px 24px", borderRadius:8, fontFamily:"Inter, sans-serif", fontWeight:700, fontSize:"0.875rem", cursor:"pointer", transition:"all 0.15s", background:a?"#c9a84c":"#1a1a1a", color:a?"#0a0a0a":"#6b7280", border:`1.5px solid ${a?"#c9a84c":"#3a3a3a"}` }),
  // Selector de aeropuerto (Maragogi etc.)
  aeroWrap:    { display:"flex", gap:8, marginTop:8, flexWrap:"wrap" },
  aeroBtn:     a => ({ padding:"7px 16px", borderRadius:8, fontFamily:"Inter, sans-serif", fontWeight:700, fontSize:"0.8rem", cursor:"pointer", background:a?"#1e3a6a":"#1a1a1a", color:a?"#93c5fd":"#6b7280", border:`1.5px solid ${a?"#3b82f6":"#3a3a3a"}` }),
  // Contador de pasajeros
  cntWrap:     { display:"flex", alignItems:"center", gap:12 },
  cntBtn:      { width:34, height:34, borderRadius:"50%", background:"#1a1a1a", border:"1px solid #3a3a3a", color:"#e5e7eb", fontSize:"1.1rem", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Inter, sans-serif" },
  cntVal:      { fontSize:"1.2rem", fontWeight:800, color:"#f9fafb", minWidth:24, textAlign:"center" },
  // Badges de noches
  nBadge:      { display:"inline-block", padding:"3px 10px", borderRadius:20, background:"#c9a84c", color:"#0a0a0a", fontSize:"0.72rem", fontWeight:800 },
  nBadgeOff:   { display:"inline-block", padding:"3px 10px", borderRadius:20, background:"#1a1a1a", border:"1px solid #3a3a3a", color:"#4b5563", fontSize:"0.72rem", fontWeight:700 },
  nBadgeTotal: { display:"inline-block", padding:"3px 10px", borderRadius:20, background:"#0f2340", border:"1px solid #c9a84c50", color:"#c9a84c", fontSize:"0.72rem", fontWeight:800 },
  // Itinerario visual
  itWrap:      { background:"#0a1628", border:"1px solid #1e3a5f", borderRadius:10, padding:"14px 20px", marginTop:14 },
  itRow:       { display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", fontSize:"0.9rem" },
  itFecha:     { fontWeight:800, color:"#c9a84c" },
  itDest:      { color:"#93c5fd", fontWeight:700 },
  itArrow:     { color:"#4b5563" },
  itNoche:     { display:"inline-block", padding:"2px 8px", borderRadius:12, background:"#c9a84c20", border:"1px solid #c9a84c40", color:"#c9a84c", fontSize:"0.68rem", fontWeight:700, marginLeft:2 },
  itTotal:     { marginTop:10, paddingTop:10, borderTop:"1px solid #1e3a5f", fontSize:"0.8rem", color:"#6b7280" },
  // Links
  linkCard:    { background:"#0a0f1a", border:"1px solid #1e3a5f", borderRadius:12, padding:"20px 24px", marginBottom:16 },
  linkTit:     c => ({ fontSize:"0.68rem", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em", color:c, marginBottom:16 }),
  btnOla:      { display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"13px 20px", borderRadius:10, border:"none", background:"#3b82f6", color:"white", fontFamily:"Inter, sans-serif", fontWeight:700, fontSize:"0.875rem", textDecoration:"none", marginBottom:6 },
  btnPN:       { display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"13px 20px", borderRadius:10, border:"1.5px solid #10b981", background:"#10b98112", color:"#10b981", fontFamily:"Inter, sans-serif", fontWeight:700, fontSize:"0.875rem", textDecoration:"none", marginBottom:8 },
  btnOff:      { opacity:0.35, pointerEvents:"none" },
  urlBox:      { marginTop:6, padding:"8px 12px", background:"#0a0a0a", borderRadius:6, fontSize:"0.7rem", color:"#4b5563", fontFamily:"monospace", overflowWrap:"break-word", wordBreak:"break-all" },
  avisoBlue:   { background:"#0d1f3a", border:"1px solid #1e3a6a", borderRadius:8, padding:"10px 14px", fontSize:"0.8rem", color:"#93c5fd", marginTop:8 },
  avisoOrange: { display:"flex", gap:8, padding:"10px 14px", background:"#1a1000", border:"1px solid #f59e0b30", borderRadius:8, fontSize:"0.78rem", color:"#f59e0b", marginTop:10 },
  // Total personas
  totalBox:    { marginTop:12, padding:"10px 14px", background:"#1a1a1a", borderRadius:8, fontSize:"0.875rem", color:"#9ca3af" },
  // Próximos pasos
  nextCard:     { background:"#0a150a", border:"1px solid #1a2a1a", borderRadius:12, padding:"20px 24px", marginBottom:16 },
  nochesInput:  { width:"100%", padding:"10px 4px", background:"#0a1628", border:"1.5px solid #c9a84c40", borderRadius:8, color:"#c9a84c", fontFamily:"Inter, sans-serif", fontSize:"1.1rem", fontWeight:800, textAlign:"center", outline:"none", boxSizing:"border-box", minWidth:0 },
  btnAdd:       { display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"11px 16px", borderRadius:8, border:"1.5px dashed #3a3a3a", background:"transparent", color:"#6b7280", fontFamily:"Inter, sans-serif", fontWeight:700, fontSize:"0.875rem", cursor:"pointer", width:"100%", boxSizing:"border-box" },
  btnRemove:    { padding:"6px 10px", borderRadius:6, border:"1px solid #3a1a1a", background:"#1a0808", color:"#ef4444", fontFamily:"Inter, sans-serif", fontWeight:700, fontSize:"0.75rem", cursor:"pointer", flexShrink:0 },
  hotelCard:    { background:"#0f0f0f", border:"1px solid #2a2a2a", borderRadius:10, padding:"14px 16px", marginBottom:10 },
  trasCard:     ok => ({ background:"#0f0f0f", border:`1px solid ${ok ? "#2a2a2a" : "#3a1a00"}`, borderRadius:10, padding:"14px 16px", marginBottom:10 }),
  lineaRes:     hi => ({ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid #1a1a1a", color:hi?"#f9fafb":"#9ca3af", fontWeight:hi?700:400, fontSize:hi?"0.9rem":"0.875rem" }),
}

const HOTEL_VACIO = { nombre: "", precioTotal: "", desayuno: true, esLibre: false }

// Los nombres de destino en DESTINOS_COTIZADOR pueden diferir levemente de las claves de HOTELES
const HOTELES_KEY_MAP = {
  "Porto de Galinhas": "Porto Galinhas",
}

function getHotelesDestino(destinoNombre) {
  const key = HOTELES_KEY_MAP[destinoNombre] || destinoNombre
  return HOTELES[key] || []
}

const INICIAL = {
  origen:            ORIGEN_DEFAULT,
  numDestinos:       1,
  destino1:          "",
  aeropuerto1:       "",
  destino2:          "",
  aeropuerto2:       "",
  fechaSalida:       "2027-01-01",
  fechaCambio:       "",
  fechaRegreso:      "2027-01-08",
  adultos:           2,
  menores:           0,
  menoresEdades:     [],
  habitaciones:      1,
  cliente:           "",
  // ── Fase 2 ────────────────────────────────────────────────────────
  vuelo: { aerolinea:"", precioTotal:"", horarioSalida:"", horarioRegreso:"", verHorarios:false },
  hoteles1:          [{ ...HOTEL_VACIO }],
  hoteles2:          [],
  trasladosManuales: {},    // { "GIG→Búzios": "120" } — override manual por segmento
  extras: {
    excursiones: { incluye: false, monto: "" },
    asistencia:  { incluye: false, monto: "" },
  },
  margen: MARGEN_DEFAULT,
  destinoFijo:  1,   // para 2 destinos: cuál queda fijo (1 o 2)
  hotelFijo1:   0,   // índice del hotel fijo cuando destinoFijo === 1
  hotelFijo2:   0,   // índice del hotel fijo cuando destinoFijo === 2
}

export default function Cotizador() {
  const { user, userData } = useAuth()
  const vendedor = userData?.nombre || user?.displayName || user?.email?.split("@")[0] || "Sergio"

  const [c, setC] = useState(INICIAL)
  const set = (k, v) => setC(p => ({...p, [k]: v}))

  // ─── Cálculo de noches ──────────────────────────────────────────────
  const noches1 = c.numDestinos === 1
    ? calcNights(c.fechaSalida, c.fechaRegreso)
    : calcNights(c.fechaSalida, c.fechaCambio)
  const noches2  = c.numDestinos === 2 ? calcNights(c.fechaCambio, c.fechaRegreso) : null
  const nochesTotal = noches1 != null && noches2 != null ? noches1 + noches2 : noches1

  // ─── Info de destinos ──────────────────────────────────────────────
  const d1Info = getDestinoInfo(c.destino1)
  const d2Info = getDestinoInfo(c.destino2)
  const d1MultiAero = !!(d1Info?.ola?.options?.length)
  const d2MultiAero = !!(d2Info?.ola?.options?.length)

  // ─── Links generados ──────────────────────────────────────────────
  // Desplaza una fecha ISO N días (noon para evitar problemas de DST)
  function shiftDate(dateStr, days) {
    if (!dateStr) return null
    const d = new Date(dateStr + "T12:00:00")
    d.setDate(d.getDate() + days)
    return d.toISOString().split("T")[0]
  }

  // 5 variantes OLA: exactas ±2 días respetando la cantidad de noches
  const olaVariantes = (c.fechaSalida && c.fechaRegreso)
    ? [-2, -1, 0, 1, 2].map(offset => {
        const salida  = shiftDate(c.fechaSalida,  offset)
        const regreso = shiftDate(c.fechaRegreso, offset)
        return {
          offset, salida, regreso,
          url: buildOlaUrl({ origenNombre:c.origen, destino1Nombre:c.destino1, fechaIda:salida, fechaVuelta:regreso, adultos:c.adultos, menores:c.menores, aeropuertoOverride:c.aeropuerto1||null }),
        }
      })
    : []

  function abrirTodosOla() {
    olaVariantes.forEach(v => { if (v.url) window.open(v.url, "_blank", "noopener") })
  }

  const pnUrl1 = buildPNUrl({
    destinoNombre: c.destino1,
    checkin:       c.fechaSalida,
    checkout:      c.numDestinos === 1 ? c.fechaRegreso : c.fechaCambio,
    adultos:       c.adultos,
    menores:       c.menores,
  })

  const pnUrl2 = c.numDestinos === 2 ? buildPNUrl({
    destinoNombre: c.destino2,
    checkin:       c.fechaCambio,
    checkout:      c.fechaRegreso,
    adultos:       c.adultos,
    menores:       c.menores,
  }) : null

  const totalPersonas = c.adultos + c.menores
  const olaFaltaAero  = c.destino1 && d1MultiAero && !c.aeropuerto1

  // ── Draft de noches: permite tipear libremente sin loops de controlled input ──
  const [noche1Draft, setNoche1Draft]     = useState(null)
  const [noche2Draft, setNoche2Draft]     = useState(null)
  const [copiado, setCopiado]             = useState({})
  const [ganTotalDraft, setGanTotalDraft] = useState(null)
  const [ganPPDraft,    setGanPPDraft]    = useState(null)
  const [grupoCotizacionId]               = useState(() =>
    `${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 8)}`
  )

  // ── Reset destino2 si deja de ser compatible al cambiar destino1 ──
  useEffect(() => {
    if (!c.destino1 || !c.destino2) return
    const compatibles = getDestinosCompatibles(c.destino1, c.aeropuerto1)
    if (!compatibles.some(d => d.nombre === c.destino2)) {
      set("destino2", ""); set("aeropuerto2", "")
    }
  }, [c.destino1, c.aeropuerto1]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Helpers menores con edades ─────────────────────────────────────
  function setMenores(newVal) {
    setC(p => {
      const edades = [...(p.menoresEdades || [])]
      const diff = newVal - p.menores
      if (diff > 0) for (let i = 0; i < diff; i++) edades.push(8)
      else edades.splice(edades.length + diff)
      return { ...p, menores: newVal, menoresEdades: edades }
    })
  }
  function setEdadMenor(idx, edad) {
    setC(p => {
      const eds = [...(p.menoresEdades || [])]
      eds[idx] = Number(edad)
      return { ...p, menoresEdades: eds }
    })
  }

  // ── Noches → actualiza la fecha final del segmento ──
  function handleNochesChange(campo, valor) {
    const n = parseInt(valor)
    if (!n || n <= 0 || n > 90) return
    if (campo === "noches1") {
      if (c.numDestinos === 1 && c.fechaSalida)      set("fechaRegreso", addNights(c.fechaSalida, n))
      else if (c.numDestinos === 2 && c.fechaSalida) set("fechaCambio",  addNights(c.fechaSalida, n))
    } else if (campo === "noches2" && c.fechaCambio) {
      set("fechaRegreso", addNights(c.fechaCambio, n))
    }
  }

  // ── Copiar parámetros de búsqueda de PN al portapapeles ──
  function copiarParamsPN(destino, checkin, checkout) {
    const fmtD  = d => d ? d.split("-").reverse().join("/") : "—"
    const dInfo = getDestinoInfo(destino)
    const edadesLinea = c.menoresEdades.length > 0
      ? "\nEdades: " + c.menoresEdades.map((e,i) => `Menor ${i+1}: ${e} años`).join("  ·  ")
      : ""
    const txt = [
      `─── PriceNavigator ───────────────────`,
      `Destino:        ${destino}`,
      `Destination ID: ${dInfo?.pn?.id || "—"}`,
      `Check-in:       ${fmtD(checkin)}`,
      `Check-out:      ${fmtD(checkout)}`,
      `Habitaciones:   ${c.habitaciones}`,
      `Adultos:        ${c.adultos}  |  Menores: ${c.menores}${edadesLinea}`,
      `Filtros:        precio ↑ · solo desayuno · favoritos · cancelación gratis`,
    ].join("\n")
    navigator.clipboard.writeText(txt).then(() => {
      setCopiado(p => ({...p, [destino]: true}))
      setTimeout(() => setCopiado(p => ({...p, [destino]: false})), 2000)
    })
  }

  // ── Gestión de hoteles ─────────────────────────────────────────────
  function addHotel(destNum) {
    const key = destNum === 1 ? "hoteles1" : "hoteles2"
    setC(p => ({...p, [key]: [...p[key], { ...HOTEL_VACIO }]}))
  }
  function removeHotel(destNum, idx) {
    const key = destNum === 1 ? "hoteles1" : "hoteles2"
    setC(p => ({...p, [key]: p[key].filter((_, i) => i !== idx)}))
  }
  function updateHotel(destNum, idx, field, val) {
    const key = destNum === 1 ? "hoteles1" : "hoteles2"
    setC(p => ({...p, [key]: p[key].map((h, i) => i === idx ? {...h, [field]: val} : h)}))
  }
  function setVuelo(field, val) {
    setC(p => ({...p, vuelo: {...p.vuelo, [field]: val}}))
  }
  function setExtra(key, field, val) {
    setC(p => ({...p, extras: {...p.extras, [key]: {...p.extras[key], [field]: val}}}))
  }
  function setOverrideTraslado(key, val) {
    setC(p => ({...p, trasladosManuales: {...p.trasladosManuales, [key]: val}}))
  }

  // ── Traslados calculados automáticamente ───────────────────────────
  const trasladosCalc = (c.destino1 && totalPersonas > 0)
    ? calcTransfers({
        destino1:    c.destino1,
        destino2:    c.numDestinos === 2 ? c.destino2 : null,
        aeropuerto1: c.aeropuerto1 || null,
        aeropuerto2: c.aeropuerto2 || null,
        numDestinos: c.numDestinos,
        adultos:     c.adultos,
        menores:     c.menores,
      })
    : []

  // ── Impuesto de agencia sobre vuelo (×1,025) ──────────────────────
  const nNum             = s => Number(String(s || "0").replace(/,/g, "")) || 0
  const precioBaseVuelo  = nNum(c.vuelo.precioTotal)
  const precioConImpVuelo = precioBaseVuelo > 0 ? Math.round(precioBaseVuelo * 1.025) : 0

  // ── Costos totales ────────────────────────────────────────────────
  // ── Costos base sin hotel (sección común a todas las variantes) ───
  const costosBase = calcCosts({
    vuelo:               { ...c.vuelo, precioTotal: precioConImpVuelo },
    hoteles1: [], hoteles2: [],
    trasladosCalculados: trasladosCalc,
    trasladosManuales:   c.trasladosManuales,
    extras:              c.extras,
    adultos:             c.adultos,
    menores:             c.menores,
    margen:              c.margen,
  })

  // ── Variantes: hoteles como alternativas, nunca acumulables ───────
  const _cv = (h1, h2) => calcCosts({
    vuelo:               { ...c.vuelo, precioTotal: precioConImpVuelo },
    hoteles1:            h1 ? [h1] : [],
    hoteles2:            h2 ? [h2] : [],
    trasladosCalculados: trasladosCalc,
    trasladosManuales:   c.trasladosManuales,
    extras:              c.extras,
    adultos:             c.adultos,
    menores:             c.menores,
    margen:              c.margen,
  })

  let variantes = []
  if (c.numDestinos === 1) {
    const arr = c.hoteles1.length > 0 ? c.hoteles1 : [{ ...HOTEL_VACIO }]
    variantes = arr.map((h, i) => ({
      i,
      hotel1: h, hotel2: null,
      label: h.nombre || `Opción ${i + 1}`,
      costos: _cv(h, null),
    }))
  } else {
    const hFixed = c.destinoFijo === 1 ? c.hoteles1 : c.hoteles2
    const hVar   = c.destinoFijo === 1 ? c.hoteles2 : c.hoteles1
    const idxFij = c.destinoFijo === 1 ? c.hotelFijo1 : c.hotelFijo2
    const hotelF = hFixed[idxFij] || { ...HOTEL_VACIO }
    const arrVar = hVar.length > 0 ? hVar : [{ ...HOTEL_VACIO }]
    variantes = arrVar.map((hv, i) => {
      const h1 = c.destinoFijo === 1 ? hotelF : hv
      const h2 = c.destinoFijo === 1 ? hv     : hotelF
      return { i, hotel1: h1, hotel2: h2, costos: _cv(h1, h2) }
    })
  }

  // Primera variante como referencia para los campos de ganancia
  const costos = variantes[0]?.costos || costosBase

  const navigate = useNavigate()

  // ── Transformación: cotizador → FORM_VACIO del generador ──────────
  function cotizadorAGenerador(variante) {
    const paxTotal = c.adultos + c.menores

    // Hotel del cotizador → formato del generador: { "Nombre": { sel, cat, precio, tachado } }
    const _n = s => Number(String(s || "0").replace(/,/g, "")) || 0
    const hotelAObj = (hotelData, destinoNombre) => {
      if (!hotelData?.nombre) return {}
      const opts = getHotelesDestino(destinoNombre)
      const info = opts.find(oh => oh.n === hotelData.nombre)
      return { [hotelData.nombre]: { sel: true, cat: info?.cat || "Intermedio", precio: _n(hotelData.precioTotal), tachado: false } }
    }

    // Aerolinea: convertir el label al key para que el generador muestre el logo
    const aeroObj = AEROLINEAS.find(a => a.label === c.vuelo.aerolinea || a.key === c.vuelo.aerolinea)
    const aeroKey = aeroObj?.key || c.vuelo.aerolinea

    // Incluye: auto-detectado a partir de los datos cargados en el cotizador
    const incluye = {
      aereos:        !!c.vuelo.aerolinea,
      hospedaje:     !!(variante.hotel1?.nombre || variante.hotel2?.nombre),
      traslados:     trasladosCalc.some(t => !t.noConfigurado && (t.monto || 0) > 0),
      comunicacion:  true,
      desayuno:      !!(variante.hotel1?.desayuno || variante.hotel2?.desayuno),
      mediaP:        false,
      allInc:        false,
      asistencia:    c.extras.asistencia.incluye,
    }

    // Precio por persona → modo "total" del generador
    const precioPP = paxTotal > 0 && variante.costos.precioFinal > 0
      ? Math.round(variante.costos.precioFinal / paxTotal)
      : null

    return {
      cliente:          c.cliente,
      fechaSalida:      c.fechaSalida,
      fechaRegreso:     c.fechaRegreso,
      ciudadSalida:     c.origen || "Buenos Aires",
      ciudadOtra:       "",
      aerolinea:        aeroKey,
      equipaje:         { mochila: true, carryOn: true, valija: false, cantValijas: 1 },
      destino1:         c.destino1,
      noches1:          String(noches1 || 7),
      hoteles1:         hotelAObj(variante.hotel1, c.destino1),
      destino1Libre:    "",
      destino2:         c.numDestinos === 2 ? c.destino2 : "",
      noches2:          String(noches2 || 7),
      hoteles2:         c.numDestinos === 2 ? hotelAObj(variante.hotel2, c.destino2) : {},
      destino2Libre:    "",
      incluye,
      regalo:           {},
      cenaCantidad:     "2",
      regaloLibreTexto: "",
      precio: precioPP
        ? { modo:"total",   total:String(precioPP), reserva:"", pax:String(paxTotal), cuotas:"6", rangoMin:"", rangoMax:"" }
        : { modo:"oculto",  total:"",               reserva:"", pax:String(paxTotal), cuotas:"6", rangoMin:"", rangoMax:"" },
    }
  }

  function buildCotizacionContext(variante) {
    const paxTotal = c.adultos + c.menores
    const n = s => Number(String(s || "0").replace(/,/g, "")) || 0

    // Snapshot de hotel para una variante y destino específico
    const hotelSnap = (hotelData, destinoNombre, nochesN) => {
      if (!hotelData?.nombre) return null
      const precioTotal = n(hotelData.precioTotal)
      const opts = getHotelesDestino(destinoNombre)
      const info = opts.find(oh => oh.n === hotelData.nombre)
      return {
        nombre:        hotelData.nombre,
        precioTotal,
        precioPorNoche: nochesN > 0 ? Math.round(precioTotal / nochesN) : null,
        desayuno:      hotelData.desayuno || false,
        esLibre:       hotelData.esLibre  || false,
        categoria:     info?.cat || null,
        habitaciones:  c.habitaciones,
      }
    }

    // Destinos con hotel de la variante elegida
    const destinos = [
      {
        orden:      1,
        nombre:     c.destino1,
        aeropuerto: getDestinoInfo(c.destino1)?.ola?.aeropuerto || null,
        checkin:    c.fechaSalida,
        checkout:   c.numDestinos === 2 ? c.fechaCambio : c.fechaRegreso,
        noches:     noches1 || null,
        hotel:      hotelSnap(variante.hotel1, c.destino1, noches1),
      },
      ...(c.numDestinos === 2 ? [{
        orden:      2,
        nombre:     c.destino2,
        aeropuerto: getDestinoInfo(c.destino2)?.ola?.aeropuerto || null,
        checkin:    c.fechaCambio,
        checkout:   c.fechaRegreso,
        noches:     noches2 || null,
        hotel:      hotelSnap(variante.hotel2, c.destino2, noches2),
      }] : []),
    ]

    // Traslados con diferenciación calculado / manual / final
    const trasladosSnap = trasladosCalc.map(t => {
      const key         = `${t.origen}→${t.destino}`
      const override    = c.trasladosManuales[key]
      const montoManual = (override !== undefined && override !== "") ? n(override) : null
      return {
        origen:         t.origen,
        destino:        t.destino,
        segmento:       t.segmento,
        tipo:           t.tipo || null,
        montoCalculado: t.monto ?? null,
        montoManual,
        montoFinal:     montoManual !== null ? montoManual : (t.monto || 0),
        noConfigurado:  t.noConfigurado || false,
        desglose: (t.desglose || []).map(d => ({
          modality:          d.tarifa?.modality       || null,
          pricingMode:       d.tarifa?.pricing_mode   || null,
          maxPax:            d.tarifa?.max_pax         || null,
          precioPorVehiculo: d.tarifa?.price_usd       || null,
          precioPorPersona:  d.tarifa?.unit_price_usd  || null,
          paxPorVehiculo:    d.paxPorVehiculo,
          cantVehiculos:     d.cantVehiculos,
        })),
      }
    })

    // Todas las variantes generadas como alternativas hoteleras
    const alternativas = variantes.map((v, idx) => {
      const hs = []
      if (v.hotel1?.nombre) hs.push({ destino: c.destino1, ...hotelSnap(v.hotel1, c.destino1, noches1) })
      if (c.numDestinos === 2 && v.hotel2?.nombre) hs.push({ destino: c.destino2, ...hotelSnap(v.hotel2, c.destino2, noches2) })
      return {
        varianteId:      idx + 1,
        hoteles:         hs,
        costoHoteles:    v.costos.costoHoteles,
        subtotal:        v.costos.subtotal,
        margenPorcentaje: c.margen,
        ganancia:        v.costos.ganancia,
        precioFinal:     v.costos.precioFinal,
        precioPorPersona: paxTotal > 0 ? Math.round(v.costos.precioFinal / paxTotal) : null,
      }
    })

    const varianteId = (variantes.findIndex(v => v.hotel1 === variante.hotel1 && v.hotel2 === variante.hotel2) ?? 0) + 1

    return {
      schemaVersion: 1,

      // ── Índices para queries ──────────────────────────────────────
      fechaCotizacion:          new Date().toISOString().split("T")[0],
      grupoCotizacionId,
      varianteId,
      estado:                   "cotizada",
      vendedor:                 { uid: user?.uid || null, nombre: vendedor },
      cliente:                  { nombre: c.cliente || null },
      origen:                   c.origen,
      destinosNombres:          [c.destino1, ...(c.numDestinos === 2 && c.destino2 ? [c.destino2] : [])],
      numDestinos:              c.numDestinos,
      fechaSalida:              c.fechaSalida,
      fechaRegreso:             c.fechaRegreso,
      adultos:                  c.adultos,
      menores:                  c.menores,
      totalPasajeros:           paxTotal,
      aerolinea:                c.vuelo.aerolinea,
      moneda:                   "USD",
      precioFinal:              variante.costos.precioFinal,
      precioPorPersona:         paxTotal > 0 ? Math.round(variante.costos.precioFinal / paxTotal) : null,
      cantidadOpcionesHoteleras: variantes.length,

      // ── Viaje ─────────────────────────────────────────────────────
      viaje: {
        origen:       c.origen,
        numDestinos:  c.numDestinos,
        fechaSalida:  c.fechaSalida,
        fechaCambio:  c.fechaCambio  || null,
        fechaRegreso: c.fechaRegreso,
        nochesTotal:  (noches1 || 0) + (noches2 || 0),
        pasajeros:    { adultos: c.adultos, menores: c.menores, total: paxTotal },
        habitaciones: c.habitaciones,
        destinos,
      },

      // ── Vuelo ─────────────────────────────────────────────────────
      vuelo: {
        aerolinea:       c.vuelo.aerolinea,
        precioBase:      precioBaseVuelo,
        impuestoAgencia: precioConImpVuelo - precioBaseVuelo,
        precioTotal:     precioConImpVuelo,
        horarioSalida:   c.vuelo.horarioSalida  || null,
        horarioRegreso:  c.vuelo.horarioRegreso || null,
        numeroVuelo:     null,
      },

      // ── Traslados ─────────────────────────────────────────────────
      traslados: trasladosSnap,

      // ── Extras ────────────────────────────────────────────────────
      extras: {
        excursiones: { incluye: c.extras.excursiones.incluye, monto: c.extras.excursiones.incluye ? n(c.extras.excursiones.monto) : 0 },
        asistencia:  { incluye: c.extras.asistencia.incluye,  monto: c.extras.asistencia.incluye  ? n(c.extras.asistencia.monto)  : 0 },
      },

      // ── Costos (snapshot de la variante elegida) ──────────────────
      costos: {
        costoVuelos:      variante.costos.costoVuelos,
        costoHoteles:     variante.costos.costoHoteles,
        costoTraslados:   variante.costos.costoTraslados,
        fde:              variante.costos.fde,
        operativo:        variante.costos.operativo,
        excursiones:      variante.costos.excursiones,
        asistencia:       variante.costos.asistencia,
        subtotal:         variante.costos.subtotal,
        margenPorcentaje: c.margen,
        ganancia:         variante.costos.ganancia,
        precioFinal:      variante.costos.precioFinal,
        precioPorPersona: paxTotal > 0 ? Math.round(variante.costos.precioFinal / paxTotal) : null,
      },

      // ── Alternativas hoteleras (todas las variantes) ──────────────
      alternativasHoteleras: alternativas,

      // ── Búsquedas utilizadas ──────────────────────────────────────
      busquedas: {
        ola: { url: olaVariantes.find(v => v.offset === 0)?.url || null },
        priceNavigator: [c.destino1, ...(c.numDestinos === 2 && c.destino2 ? [c.destino2] : [])].map((dest, idx) => ({
          destino:       dest,
          destinationId: getDestinoInfo(dest)?.pn?.id || null,
          checkin:       idx === 0 ? c.fechaSalida : c.fechaCambio,
          checkout:      idx === 0 && c.numDestinos === 1 ? c.fechaRegreso : idx === 0 ? c.fechaCambio : c.fechaRegreso,
          adultos:       c.adultos,
          menores:       c.menores,
          habitaciones:  c.habitaciones,
          filtros:       { sort:"price-asc", boards:"2", favorites:true, freeCancellation:true },
        })),
      },
    }
  }

  function abrirEnGenerador(variante) {
    navigate("/generador", {
      state: {
        prefill:            cotizadorAGenerador(variante),
        cotizacionContext:  buildCotizacionContext(variante),
      },
    })
  }

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <div style={{ background:"#0a0a0a", minHeight:"100vh", paddingBottom:60 }}>
      <div style={S.page}>

        {/* ── Header ── */}
        <div style={S.titulo}>✈️ Nuevo cotizador</div>
        <div style={S.sub}>Cotizador de viajes · Low Travel</div>

        {/* ── Identificación ── */}
        <div style={S.card}>
          <div style={S.cardTit}>Identificación</div>
          <div style={S.twoCol}>
            <div>
              <label style={S.lbl}>Vendedor</label>
              <div style={{ ...S.input, color:"#c9a84c", fontWeight:700, cursor:"default" }}>{vendedor}</div>
            </div>
            <div>
              <label style={S.lbl}>Cliente (opcional)</label>
              <input style={S.input} type="text" placeholder="Nombre del cliente"
                value={c.cliente} onChange={e => set("cliente", e.target.value)} />
            </div>
          </div>
        </div>

        {/* ── Origen ── */}
        <div style={S.card}>
          <div style={S.cardTit}>Origen</div>
          <div style={S.field}>
            <label style={S.lbl}>Ciudad de salida</label>
            <select style={S.select} value={c.origen} onChange={e => set("origen", e.target.value)}>
              {ORIGENES.map(o => <option key={o.nombre} value={o.nombre}>{o.nombre}</option>)}
            </select>
          </div>
        </div>

        {/* ── Destinos ── */}
        <div style={S.card}>
          <div style={S.cardTit}>Destinos</div>

          <div style={S.field}>
            <label style={{ ...S.lbl, marginBottom:10 }}>¿Cuántos destinos?</label>
            <div style={S.toggleWrap}>
              <button style={S.toggleBtn(c.numDestinos === 1)}
                onClick={() => setC(p => ({
                  ...p, numDestinos:1, destino2:"", aeropuerto2:"", fechaCambio:"",
                  fechaRegreso: addNights(p.fechaSalida, 7) || p.fechaRegreso,
                }))}>
                1 destino
              </button>
              <button style={S.toggleBtn(c.numDestinos === 2)}
                onClick={() => setC(p => ({
                  ...p, numDestinos:2,
                  fechaCambio:  addNights(p.fechaSalida, 4) || "",
                  fechaRegreso: addNights(p.fechaSalida, 7) || p.fechaRegreso,
                }))}>
                2 destinos
              </button>
            </div>
          </div>

          <div style={S.divider} />

          {/* Destino 1 */}
          <div style={S.field}>
            <label style={S.lbl}>Destino 1</label>
            <select style={S.select} value={c.destino1}
              onChange={e => { set("destino1", e.target.value); set("aeropuerto1", "") }}>
              <option value="">Seleccionar</option>
              {DESTINOS_COTIZADOR.filter(d => d.nombre !== c.destino2).map(d =>
                <option key={d.nombre} value={d.nombre}>{d.nombre}</option>
              )}
            </select>
            {c.destino1 && d1MultiAero && (
              <>
                <label style={{ ...S.lbl, marginTop:10 }}>Aeropuerto de conexión</label>
                <div style={S.aeroWrap}>
                  {d1Info.ola.options.map(opt => (
                    <button key={opt.aeropuerto} style={S.aeroBtn(c.aeropuerto1 === opt.aeropuerto)}
                      onClick={() => set("aeropuerto1", opt.aeropuerto)}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Destino 2 */}
          {c.numDestinos === 2 && (
            <div style={S.field}>
              <label style={S.lbl}>Destino 2</label>
              <select style={S.select} value={c.destino2}
                onChange={e => { set("destino2", e.target.value); set("aeropuerto2", "") }}>
                <option value="">Seleccionar</option>
                {getDestinosCompatibles(c.destino1, c.aeropuerto1).map(d =>
                  <option key={d.nombre} value={d.nombre}>{d.nombre}</option>
                )}
              </select>
              {c.destino2 && d2MultiAero && (
                <>
                  <label style={{ ...S.lbl, marginTop:10 }}>Aeropuerto de conexión</label>
                  <div style={S.aeroWrap}>
                    {d2Info.ola.options.map(opt => (
                      <button key={opt.aeropuerto} style={S.aeroBtn(c.aeropuerto2 === opt.aeropuerto)}
                        onClick={() => set("aeropuerto2", opt.aeropuerto)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Fechas ── */}
        <div style={S.card}>
          <div style={S.cardTit}>Fechas e itinerario</div>

          {c.numDestinos === 1 ? (
            // ── 1 destino: salida | noches | regreso ─────────────────────────
            <div style={{ display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap", marginBottom:12 }}>
              <div style={{ flex:"2 1 130px" }}>
                <label style={S.lbl}>Fecha de salida</label>
                <input type="date" style={S.input} value={c.fechaSalida}
                  onChange={e => set("fechaSalida", e.target.value)} />
              </div>
              <div style={{ flex:"0 0 76px" }}>
                <label style={{ ...S.lbl, textAlign:"center" }}>Noches</label>
                <input
                  type="number" min="1" max="90" style={S.nochesInput}
                  value={noche1Draft !== null ? noche1Draft : (noches1 ?? "")}
                  placeholder="—"
                  onFocus={() => setNoche1Draft(noches1 ?? "")}
                  onChange={e => setNoche1Draft(e.target.value)}
                  onBlur={e => { handleNochesChange("noches1", e.target.value); setNoche1Draft(null) }}
                />
              </div>
              <div style={{ flex:"2 1 130px" }}>
                <label style={S.lbl}>Fecha de regreso</label>
                <input type="date" style={S.input} value={c.fechaRegreso}
                  min={c.fechaSalida || undefined}
                  onChange={e => set("fechaRegreso", e.target.value)} />
              </div>
            </div>
          ) : (
            // ── 2 destinos: salida | n1 | cambio | n2 | regreso ──────────────
            <>
              <div style={{ display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap", marginBottom:12 }}>
                <div style={{ flex:"2 1 110px" }}>
                  <label style={S.lbl}>Salida</label>
                  <input type="date" style={S.input} value={c.fechaSalida}
                    onChange={e => set("fechaSalida", e.target.value)} />
                </div>
                <div style={{ flex:"0 0 68px" }}>
                  <label style={{ ...S.lbl, textAlign:"center", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                    {c.destino1 ? c.destino1.split(" ")[0] : "D1"}
                  </label>
                  <input
                    type="number" min="1" max="90" style={S.nochesInput}
                    value={noche1Draft !== null ? noche1Draft : (noches1 ?? "")}
                    placeholder="—"
                    onFocus={() => setNoche1Draft(noches1 ?? "")}
                    onChange={e => setNoche1Draft(e.target.value)}
                    onBlur={e => { handleNochesChange("noches1", e.target.value); setNoche1Draft(null) }}
                  />
                </div>
                <div style={{ flex:"2 1 110px" }}>
                  <label style={S.lbl}>Cambio de destino</label>
                  <input type="date" style={S.input} value={c.fechaCambio}
                    min={c.fechaSalida || undefined}
                    onChange={e => set("fechaCambio", e.target.value)} />
                </div>
                <div style={{ flex:"0 0 68px" }}>
                  <label style={{ ...S.lbl, textAlign:"center", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                    {c.destino2 ? c.destino2.split(" ")[0] : "D2"}
                  </label>
                  <input
                    type="number" min="1" max="90" style={S.nochesInput}
                    value={noche2Draft !== null ? noche2Draft : (noches2 ?? "")}
                    placeholder="—"
                    onFocus={() => setNoche2Draft(noches2 ?? "")}
                    onChange={e => setNoche2Draft(e.target.value)}
                    onBlur={e => { handleNochesChange("noches2", e.target.value); setNoche2Draft(null) }}
                  />
                </div>
                <div style={{ flex:"2 1 110px" }}>
                  <label style={S.lbl}>Regreso</label>
                  <input type="date" style={S.input} value={c.fechaRegreso}
                    min={c.fechaCambio || c.fechaSalida || undefined}
                    onChange={e => set("fechaRegreso", e.target.value)} />
                </div>
              </div>

              {/* Resumen de noches (2 destinos) */}
              <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:8 }}>
                {[[c.destino1 || "Destino 1", noches1],[c.destino2 || "Destino 2", noches2]].map(([label,n]) => (
                  <div key={label} style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:"0.9rem", color:"#63b7dd" }}>{label}:</span>
                    {n != null ? <span style={S.nBadge}>{n} noches</span> : <span style={S.nBadgeOff}>—</span>}
                  </div>
                ))}
                {nochesTotal != null && noches2 != null && (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:"0.9rem", color:"#88d88c" }}>Total:</span>
                    <span style={S.nBadgeTotal}>{nochesTotal} noches</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Itinerario visual */}
          {/*(c.destino1 || c.fechaSalida) && (
            <div style={S.itWrap}>
              <div style={S.itRow}>
                <span style={S.itFecha}>{fmtDDMM(c.fechaSalida)}</span>
                <span style={S.itArrow}>→</span>
                <span style={S.itDest}>{c.destino1 || "Destino 1"}</span>
                {noches1 != null && <span style={S.itNoche}>{noches1} n</span>}
                {c.numDestinos === 2 && (
                  <>
                    <span style={S.itArrow}>→</span>
                    <span style={S.itFecha}>{fmtDDMM(c.fechaCambio)}</span>
                    <span style={S.itArrow}>→</span>
                    <span style={S.itDest}>{c.destino2 || "Destino 2"}</span>
                    {noches2 != null && <span style={S.itNoche}>{noches2} n</span>}
                  </>
                )}
                <span style={S.itArrow}>→</span>
                <span style={S.itFecha}>{fmtDDMM(c.fechaRegreso)}</span>
              </div>
              {nochesTotal != null && (
                <div style={S.itTotal}>
                  Total del viaje: <strong style={{ color:"#c9a84c" }}>{nochesTotal} noches</strong>
                </div>
              )}
            </div>
          )*/}
        </div>

        {/* ── Pasajeros ── */}
        <div style={S.card}>
          <div style={S.cardTit}>Pasajeros y habitaciones</div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
            <div>
              <label style={S.lbl}>Adultos (≥ 12 años)</label>
              <div style={S.cntWrap}>
                <button style={S.cntBtn} onClick={() => set("adultos", Math.max(1, c.adultos - 1))}>−</button>
                <span style={S.cntVal}>{c.adultos}</span>
                <button style={S.cntBtn} onClick={() => set("adultos", Math.min(20, c.adultos + 1))}>+</button>
              </div>
            </div>
            <div>
              <label style={S.lbl}>Menores ({"<"} 12 años)</label>
              <div style={S.cntWrap}>
                <button style={S.cntBtn} onClick={() => setMenores(Math.max(0, c.menores - 1))}>−</button>
                <span style={S.cntVal}>{c.menores}</span>
                <button style={S.cntBtn} onClick={() => setMenores(Math.min(10, c.menores + 1))}>+</button>
              </div>
            </div>
            <div>
              <label style={S.lbl}>Habitaciones</label>
              <div style={S.cntWrap}>
                <button style={S.cntBtn} onClick={() => set("habitaciones", Math.max(1, c.habitaciones - 1))}>−</button>
                <span style={S.cntVal}>{c.habitaciones}</span>
                <button style={S.cntBtn} onClick={() => set("habitaciones", Math.min(10, c.habitaciones + 1))}>+</button>
              </div>
            </div>
          </div>

          {c.menores > 0 && (
            <div style={{ marginBottom:12 }}>
              <label style={S.lbl}>Edades de los menores</label>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:6 }}>
                {c.menoresEdades.map((edad, i) => (
                  <div key={i} style={{ textAlign:"center" }}>
                    <div style={{ fontSize:"0.65rem", color:"#4b5563", marginBottom:4 }}>Menor {i+1}</div>
                    <input type="number" min="0" max="17" style={{ ...S.input, width:60, textAlign:"center", padding:"8px 4px" }}
                      value={edad} onChange={e => setEdadMenor(i, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={S.totalBox}>
            Total: <strong style={{ color:"#f9fafb" }}>
              {totalPersonas} {totalPersonas === 1 ? "persona" : "personas"}
            </strong>
            <span style={{ color:"#6b7280", marginLeft:8 }}>
              ({c.adultos} adulto{c.adultos !== 1 ? "s" : ""}
              {c.menores > 0 && ` + ${c.menores} menor${c.menores !== 1 ? "es" : ""}`}
              {" · "}{c.habitaciones} habitación{c.habitaciones !== 1 ? "es" : ""})
            </span>
          </div>
        </div>

        {/* ── OLA ── */}
        <div style={S.linkCard}>
          <div style={S.linkTit("#3b82f6")}>✈️ Buscar vuelos — OLA</div>

          {olaFaltaAero && (
            <div style={S.avisoBlue}>
              ⚠️ Seleccioná el aeropuerto de conexión para <strong>{c.destino1}</strong> para generar los links.
            </div>
          )}
          {!c.destino1 && !olaFaltaAero && (
            <div style={S.avisoBlue}>Seleccioná un destino para generar los links de OLA.</div>
          )}

          {olaVariantes.length > 0 && (
            <>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:6, marginBottom:8 }}>
                {olaVariantes.map(({ offset, salida, regreso, url }) => {
                  const es0 = offset === 0
                  const labelOffset = offset === 0 ? "✓ exactas"
                    : offset > 0 ? `+${offset}d` : `${offset}d`
                  return (
                    <a key={offset} href={url || undefined} target="_blank" rel="noopener noreferrer"
                      style={{
                        display:"flex", flexDirection:"column", alignItems:"center", gap:2,
                        padding:"10px 4px", borderRadius:8, textDecoration:"none",
                        background: es0 ? "#1e3a6a" : "#111",
                        border:     es0 ? "2px solid #3b82f6" : "1px solid #2a2a2a",
                        color:      es0 ? "#93c5fd" : "#6b7280",
                        opacity: url ? 1 : 0.35,
                        pointerEvents: url ? "auto" : "none",
                      }}
                      onClick={e => { if (!url) e.preventDefault() }}>
                      <span style={{ fontSize:"0.6rem", fontWeight:700, color: es0 ? "#60a5fa" : "#4b5563" }}>
                        {labelOffset}
                      </span>
                      <span style={{ fontSize:"0.78rem", fontWeight:800 }}>{fmtDDMM(salida)}</span>
                      <span style={{ fontSize:"0.65rem", color:"#4b5563" }}>al</span>
                      <span style={{ fontSize:"0.78rem", fontWeight:800 }}>{fmtDDMM(regreso)}</span>
                    </a>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* ── PriceNavigator ── */}
        {c.destino1 && (
          <div style={S.linkCard}>
            <div style={S.linkTit("#10b981")}>🏨 PriceNavigator — Hoteles</div>

            {[
              { destino:c.destino1, checkin:c.fechaSalida,  checkout:c.numDestinos===1 ? c.fechaRegreso : c.fechaCambio, noches:noches1 },
              ...(c.numDestinos===2 && c.destino2
                ? [{ destino:c.destino2, checkin:c.fechaCambio, checkout:c.fechaRegreso, noches:noches2 }]
                : [])
            ].map(({ destino, checkin, checkout, noches }) => {
              const dInfo     = getDestinoInfo(destino)
              const fmtD      = d => d ? d.split("-").reverse().join("/") : "—"
              const edadesStr = c.menoresEdades.length > 0
                ? c.menoresEdades.map((e,i) => `Menor ${i+1}: ${e} años`).join("  ·  ")
                : null

              return (
                <div key={destino} style={{ marginBottom:16, paddingBottom:16, borderBottom:"1px solid #0f2018" }}>

                  {/* Botón + copiar */}
                  <div style={{ display:"flex", gap:8, marginBottom:12, alignItems:"stretch" }}>
                    <a href="https://lowtravel.app.pricenavigator.net/v3/home/hotels"
                      target="_blank" rel="noopener noreferrer"
                      style={{ ...S.btnPN, flex:1, marginBottom:0 }}>
                      🏨 Abrir PriceNavigator — {destino} ↗
                    </a>
                  </div>

                  {/* Panel de parámetros */}
                  <div style={{ background:"#060e0a", border:"1px solid #0f2018", borderRadius:10, overflow:"hidden", fontSize:"0.875rem" }}>

                    {/* Destino */}
                    <div style={{ padding:"10px 14px", borderBottom:"1px solid #0f2018" }}>
                      <div style={{ fontSize:"0.6rem", fontWeight:800, color:"#10b981", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>📍 Destino</div>
                      <div style={S.lbl}>Nombre</div>
                      <div style={{ color:"#f9fafb", fontWeight:700 }}>{destino}</div>
                    </div>

                    {/* Fechas */}
                    <div style={{ padding:"10px 14px", borderBottom:"1px solid #0f2018" }}>
                      <div style={{ fontSize:"0.6rem", fontWeight:800, color:"#10b981", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>📅 Fechas</div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                        <div>
                          <div style={S.lbl}>Check-in</div>
                          <div style={{ color:"#f9fafb", fontWeight:700 }}>{fmtD(checkin)}</div>
                        </div>
                        <div>
                          <div style={S.lbl}>Check-out</div>
                          <div style={{ color:"#f9fafb", fontWeight:700 }}>{fmtD(checkout)}</div>
                        </div>
                        <div>
                          <div style={S.lbl}>Noches</div>
                          <div style={{ color: noches ? "#c9a84c" : "#4b5563", fontWeight:700 }}>{noches ?? "—"}</div>
                        </div>
                      </div>
                    </div>

                    {/* Pasajeros */}
                    <div style={{ padding:"10px 14px" }}>
                      <div style={{ fontSize:"0.6rem", fontWeight:800, color:"#10b981", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>👥 Pasajeros y habitaciones</div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom: edadesStr ? 10 : 0 }}>
                        <div>
                          <div style={S.lbl}>Habitaciones</div>
                          <div style={{ color:"#f9fafb", fontWeight:700 }}>{c.habitaciones}</div>
                        </div>
                        <div>
                          <div style={S.lbl}>Adultos</div>
                          <div style={{ color:"#f9fafb", fontWeight:700 }}>{c.adultos}</div>
                        </div>
                        <div>
                          <div style={S.lbl}>Menores</div>
                          <div style={{ color: c.menores ? "#f9fafb" : "#4b5563", fontWeight:700 }}>{c.menores || "—"}</div>
                        </div>
                      </div>
                      {edadesStr && (
                        <div style={{ padding:"8px 10px", background:"#0a1a10", borderRadius:6, fontSize:"0.78rem", color:"#6eb89a", fontWeight:600 }}>
                          {edadesStr}
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* ── VUELOS ─────────────────────────────────────────────────── */}
        <div style={S.card}>
          <div style={S.cardTit}>✈️ Vuelos</div>
          <div style={{ fontSize:"0.75rem", color:"#4b5563", marginBottom:14 }}>
            El precio es el total para todos los pasajeros, no por persona.
          </div>
          <div style={S.twoCol}>
            <div>
              <label style={S.lbl}>Aerolínea</label>
              <select style={S.select} value={c.vuelo.aerolinea} onChange={e => setVuelo("aerolinea", e.target.value)}>
                <option value="">Seleccionar</option>
                {AEROLINEAS.map(a => <option key={a.key} value={a.label}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label style={S.lbl}>Precio total (USD) — todos los pasajeros</label>
              <input type="text" style={S.input} placeholder="Ej: 2400"
                value={c.vuelo.precioTotal} onChange={e => setVuelo("precioTotal", e.target.value)} />
              {precioConImpVuelo > 0 && (
                <div style={{ marginTop:6, padding:"8px 12px", background:"#0a1a10", border:"1px solid #10b98130", borderRadius:6, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:"0.72rem", color:"#6b7280", fontWeight:600 }}>Con impuestos agencia (×1,025)</span>
                  <span style={{ fontSize:"0.875rem", fontWeight:800, color:"#10b981" }}>
                    USD {precioConImpVuelo.toLocaleString("es-AR")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Horarios — desactivados por default */}
          <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", marginTop:4, marginBottom: c.vuelo.verHorarios ? 12 : 0 }}>
            <input type="checkbox" checked={c.vuelo.verHorarios || false}
              onChange={e => setVuelo("verHorarios", e.target.checked)}
              style={{ width:15, height:15, accentColor:"#c9a84c" }} />
            <span style={{ fontSize:"0.8rem", color: c.vuelo.verHorarios ? "#9ca3af" : "#4b5563", fontWeight:600 }}>
              Cargar horarios de vuelo
            </span>
          </label>

          {c.vuelo.verHorarios && (
            <div style={S.twoCol}>
              <div>
                <label style={S.lbl}>Horario salida</label>
                <input type="text" style={S.input} placeholder="Ej: BUE 22:10 / GIG 06:30"
                  value={c.vuelo.horarioSalida} onChange={e => setVuelo("horarioSalida", e.target.value)} />
              </div>
              <div>
                <label style={S.lbl}>Horario regreso</label>
                <input type="text" style={S.input} placeholder="Ej: GIG 14:00 / BUE 21:45"
                  value={c.vuelo.horarioRegreso} onChange={e => setVuelo("horarioRegreso", e.target.value)} />
              </div>
            </div>
          )}
        </div>

        {/* ── HOTELES ─────────────────────────────────────────────────── */}
        {c.destino1 && (
          <div style={S.card}>
            <div style={S.cardTit}>🏨 Hoteles</div>
            <div style={{ fontSize:"0.75rem", color:"#4b5563", marginBottom:16 }}>
              Precio = total de todas las habitaciones durante toda la estadía (no por noche).
            </div>

            {[
              { num:1, label:c.destino1, noches:noches1, hoteles:c.hoteles1 },
              ...(c.numDestinos === 2 && c.destino2
                ? [{ num:2, label:c.destino2, noches:noches2, hoteles:c.hoteles2 }]
                : [])
            ].map(({ num, label, noches, hoteles }) => {
              const opcionesHotel = getHotelesDestino(label)

              return (
                <div key={num} style={{ marginBottom: num < c.numDestinos ? 20 : 0 }}>

                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12, flexWrap:"wrap" }}>
                    <span style={{ fontSize:"0.875rem", fontWeight:700, color:"#e5e7eb" }}>{label}</span>
                    {noches != null && <span style={S.nBadge}>{noches} noches</span>}
                    {opcionesHotel.length > 0 && (
                      <span style={{ fontSize:"0.65rem", color:"#4b5563", fontWeight:600 }}>
                        · {opcionesHotel.length} hoteles pre-cargados
                      </span>
                    )}
                  </div>

                  {hoteles.map((h, i) => {
                    const tieneOpciones = opcionesHotel.length > 0
                    const infoHotel     = opcionesHotel.find(oh => oh.n === h.nombre)

                    return (
                      <div key={i} style={S.hotelCard}>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:8, marginBottom:10, alignItems:"start" }}>
                          <div>
                            <label style={S.lbl}>Opción {i + 1}</label>

                            {/* Selector de lista cuando hay opciones y no es modo libre */}
                            {tieneOpciones && !h.esLibre ? (
                              <>
                                <select style={S.select} value={h.nombre || ""}
                                  onChange={e => {
                                    if (e.target.value === "__libre__") {
                                      updateHotel(num, i, "esLibre", true)
                                      updateHotel(num, i, "nombre",  "")
                                    } else {
                                      updateHotel(num, i, "esLibre", false)
                                      updateHotel(num, i, "nombre",  e.target.value)
                                    }
                                  }}>
                                  <option value="">Seleccionar hotel...</option>
                                  {["Económico", "Intermedio", "Exclusivo"].map(cat => {
                                    const grupo = opcionesHotel.filter(oh => oh.cat === cat)
                                    if (!grupo.length) return null
                                    return (
                                      <optgroup key={cat} label={`── ${cat}`}>
                                        {grupo.map(oh => (
                                          <option key={oh.n} value={oh.n}>{oh.n}</option>
                                        ))}
                                      </optgroup>
                                    )
                                  })}
                                  <option value="__libre__">✏️ Otro hotel...</option>
                                </select>

                                {/* Info del hotel seleccionado */}
                                {infoHotel && (
                                  <div style={{ marginTop:6, padding:"8px 10px", background:"#0a0a0a", border:"1px solid #1a1a1a", borderRadius:6 }}>
                                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:4 }}>
                                      <span style={{
                                        fontSize:"0.58rem", fontWeight:800, padding:"2px 8px", borderRadius:10, textTransform:"uppercase", letterSpacing:"0.08em",
                                        ...(infoHotel.cat === "Económico"  ? { background:"#0a1f30", color:"#3b82f6", border:"1px solid #1e3a5f"  } :
                                            infoHotel.cat === "Intermedio" ? { background:"#0a200a", color:"#10b981", border:"1px solid #1a3a1a" } :
                                                                              { background:"#1a1500", color:"#f59e0b", border:"1px solid #3a3000" })
                                      }}>{infoHotel.cat}</span>
                                      <span style={{ fontSize:"0.78rem", color:"#9ca3af", fontWeight:600 }}>{infoHotel.u}</span>
                                    </div>
                                    {infoHotel.d && (
                                      <div style={{ fontSize:"0.72rem", color:"#4b5563", lineHeight:1.5 }}>{infoHotel.d}</div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              /* Modo texto libre */
                              <>
                                <input type="text" style={S.input} placeholder="Nombre del hotel"
                                  value={h.nombre} onChange={e => updateHotel(num, i, "nombre", e.target.value)} />
                                {tieneOpciones && (
                                  <button
                                    onClick={() => { updateHotel(num, i, "esLibre", false); updateHotel(num, i, "nombre", "") }}
                                    style={{ background:"none", border:"none", color:"#4b5563", fontSize:"0.72rem", cursor:"pointer", padding:"5px 0", textAlign:"left", fontFamily:"Inter, sans-serif" }}>
                                    ← Volver a la lista de hoteles
                                  </button>
                                )}
                              </>
                            )}
                          </div>

                          {hoteles.length > 1 && (
                            <button style={{ ...S.btnRemove, marginTop:22 }} onClick={() => removeHotel(num, i)}>✕</button>
                          )}
                        </div>

                        <div style={S.twoCol}>
                          <div>
                            <label style={S.lbl}>Precio total estadía (USD)</label>
                            <input type="text" style={S.input} placeholder="Ej: 1400"
                              value={h.precioTotal} onChange={e => updateHotel(num, i, "precioTotal", e.target.value)} />
                          </div>
                          <div style={{ display:"flex", alignItems:"flex-end", paddingBottom:6 }}>
                            <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                              <input type="checkbox" checked={h.desayuno}
                                onChange={e => updateHotel(num, i, "desayuno", e.target.checked)}
                                style={{ width:16, height:16, accentColor:"#c9a84c" }} />
                              <span style={{ fontSize:"0.875rem", color:"#9ca3af", fontWeight:600 }}>Incluye desayuno</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )
                  })}

                  <button style={S.btnAdd} onClick={() => addHotel(num)}>
                    + Agregar opción de hotel para {label}
                  </button>

                  {num < c.numDestinos && (
                    <div style={{ height:1, background:"#1f1f1f", margin:"20px 0 16px" }} />
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* ── TRASLADOS ────────────────────────────────────────────────── */}
        {c.destino1 && (
          <div style={S.card}>
            <div style={S.cardTit}>🚐 Traslados</div>
            <div style={{ fontSize:"0.75rem", color:"#4b5563", marginBottom:16 }}>
              Todas las tarifas son de solo ida. El sistema detecta los segmentos según el itinerario.
            </div>

            {trasladosCalc.length === 0 && (
              <div style={S.avisoBlue}>Completá los destinos y la cantidad de pasajeros para calcular los traslados.</div>
            )}

            {trasladosCalc.map((t, i) => {
              const key          = `${t.origen}→${t.destino}`
              const override     = c.trasladosManuales[key]
              const valorMostrar = override !== undefined ? override : (t.monto != null ? String(t.monto) : "")
              const segLabel     = { llegada:"Llegada", regreso:"Regreso", interdestino:"Entre destinos" }[t.segmento] || t.segmento

              return (
                <div key={i} style={S.trasCard(!t.noConfigurado)}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:16, alignItems:"center" }}>
                    <div>
                      <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:6, flexWrap:"wrap" }}>
                        <span style={{ fontSize:"0.6rem", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.08em", padding:"2px 8px", borderRadius:10,
                          color:    t.noConfigurado ? "#f59e0b" : "#10b981",
                          background: t.noConfigurado ? "#1a1000" : "#0a1a0a",
                          border:   `1px solid ${t.noConfigurado ? "#f59e0b40" : "#10b98140"}` }}>
                          {segLabel}
                        </span>
                        {!t.noConfigurado && t.tipo && (
                          <span style={{ fontSize:"0.68rem", color:"#4b5563", fontWeight:600, textTransform:"capitalize" }}>{t.tipo}</span>
                        )}
                      </div>
                      <div style={{ fontSize:"0.9rem", fontWeight:700, color:"#e5e7eb" }}>{t.label}</div>
                      {t.noConfigurado && (
                        <div style={{ fontSize:"0.75rem", color:"#f59e0b", marginTop:4 }}>
                          ⚠️ Sin tarifa configurada — ingresá el monto manualmente
                        </div>
                      )}
                      {!t.noConfigurado && t.desglose?.length > 0 && (
                        <div style={{ fontSize:"0.68rem", color:"#4b5563", marginTop:5, lineHeight:1.6 }}>
                          {t.desglose.map((d, i) => {
                            const desc = d.tarifa.pricing_mode === "per_person"
                              ? `${d.cantVehiculos > 1 ? `${d.cantVehiculos} × ` : ""}${d.tarifa.modality} $${d.tarifa.unit_price_usd}/p`
                              : `${d.cantVehiculos > 1 ? `${d.cantVehiculos} × ` : ""}${d.tarifa.modality} cap.${d.tarifa.max_pax}`
                            return <span key={i}>{i > 0 ? <span style={{ color:"#3a3a3a" }}> + </span> : ""}{desc}</span>
                          })}
                        </div>
                      )}
                    </div>
                    <div style={{ minWidth:120 }}>
                      <label style={S.lbl}>USD (solo ida)</label>
                      <input type="text" style={{ ...S.input, textAlign:"right", fontWeight:700 }}
                        placeholder={t.monto != null ? `Auto: ${t.monto}` : "Manual"}
                        value={valorMostrar}
                        onChange={e => setOverrideTraslado(key, e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )
            })}

            {trasladosCalc.length > 0 && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"12px 0 0", borderTop:"1px solid #1f1f1f" }}>
                <span style={{ fontWeight:700, color:"#9ca3af", fontSize:"0.875rem" }}>Total traslados</span>
                <span style={{ fontWeight:800, color:"#10b981" }}>USD {costos.costoTraslados.toLocaleString("es-AR")}</span>
              </div>
            )}
          </div>
        )}

        {/* ── EXTRAS ──────────────────────────────────────────────────── */}
        <div style={S.card}>
          <div style={S.cardTit}>⭐ Extras</div>

          {[
            ["excursiones", "🗺️ Excursiones",         "Monto total excursiones (USD)"],
            ["asistencia",  "🛡️ Asistencia al viajero","Costo de la asistencia (USD)" ],
          ].map(([key, label, ph]) => (
            <div key={key} style={{ ...S.hotelCard, marginBottom:10, border:`1px solid ${c.extras[key].incluye ? "#2a3a2a" : "#2a2a2a"}` }}>
              <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", marginBottom: c.extras[key].incluye ? 12 : 0 }}>
                <input type="checkbox" checked={c.extras[key].incluye}
                  onChange={e => setExtra(key, "incluye", e.target.checked)}
                  style={{ width:16, height:16, accentColor:"#c9a84c" }} />
                <span style={{ fontWeight:700, color: c.extras[key].incluye ? "#e5e7eb" : "#6b7280", fontSize:"0.9rem" }}>
                  {label}
                </span>
              </label>
              {c.extras[key].incluye && (
                <input type="text" style={S.input} placeholder={ph}
                  value={c.extras[key].monto}
                  onChange={e => setExtra(key, "monto", e.target.value)} />
              )}
            </div>
          ))}
        </div>

          {/* ── SELECTOR DE DESTINO FIJO (solo 2 destinos) ──────────────── */}
        {c.numDestinos === 2 && c.destino1 && c.destino2 && (
          <div style={S.card}>
            <div style={S.cardTit}>🎯 Configuración de variantes</div>

            <div style={{ marginBottom:14 }}>
              <label style={S.lbl}>¿Qué destino queda fijo?</label>
              <div style={S.toggleWrap}>
                {[{ num:1, label:c.destino1 }, { num:2, label:c.destino2 }].map(d => (
                  <button key={d.num} style={S.toggleBtn(c.destinoFijo === d.num)}
                    onClick={() => set("destinoFijo", d.num)}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom:12 }}>
              <label style={S.lbl}>
                Hotel fijo en <strong style={{ color:"#e5e7eb", textTransform:"none" }}>
                  {c.destinoFijo === 1 ? c.destino1 : c.destino2}
                </strong>
              </label>
              {(c.destinoFijo === 1 ? c.hoteles1 : c.hoteles2).length === 0 ? (
                <div style={S.avisoBlue}>Cargá al menos un hotel en ese destino primero.</div>
              ) : (
                <select style={S.select}
                  value={c.destinoFijo === 1 ? c.hotelFijo1 : c.hotelFijo2}
                  onChange={e => set(c.destinoFijo === 1 ? "hotelFijo1" : "hotelFijo2", Number(e.target.value))}>
                  {(c.destinoFijo === 1 ? c.hoteles1 : c.hoteles2).map((h, i) => (
                    <option key={i} value={i}>{h.nombre || `Opción ${i + 1}`}</option>
                  ))}
                </select>
              )}
            </div>

            <div style={S.avisoBlue}>
              Se genera una variante por cada hotel de <strong>
                {c.destinoFijo === 1 ? c.destino2 : c.destino1}
              </strong>, combinado con el hotel fijo elegido.
            </div>
          </div>
        )}

        {/* ── RESUMEN Y PRECIO FINAL ──────────────────────────────────── */}
        <div style={{ ...S.card, background:"#060e18", border:"1px solid #1e3a5f" }}>
          <div style={{ ...S.cardTit, color:"#3b82f6" }}>💰 Resumen y precio final</div>

          {/* Costos comunes — igual para todas las variantes */}
          {[
            ["✈️ Vuelos",    costosBase.costoVuelos],
            ["🚐 Traslados", costosBase.costoTraslados],
            [`📋 FDE`,       costosBase.fde],
            [`👥 Costo op. (${totalPersonas} × USD ${COSTO_OP_POR_PERSONA})`, costosBase.operativo],
            ...(costosBase.excursiones > 0 ? [["🗺️ Excursiones", costosBase.excursiones]] : []),
            ...(costosBase.asistencia  > 0 ? [["🛡️ Asistencia",  costosBase.asistencia]]  : []),
          ].map(([label, val]) => (
            <div key={label} style={S.lineaRes(false)}>
              <span>{label}</span>
              <span style={{ color:"#e5e7eb" }}>USD {Math.round(val).toLocaleString("es-AR")}</span>
            </div>
          ))}

          <div style={S.lineaRes(true)}>
            <span>
              Subtotal base
              <span style={{ fontSize:"0.7rem", fontWeight:400, color:"#4b5563", marginLeft:6 }}>
                (sin hotel — cada variante lo suma por separado)
              </span>
            </span>
            <span>USD {Math.round(costosBase.subtotal).toLocaleString("es-AR")}</span>
          </div>

          {/* Ganancia — 3 campos editables y sincronizados */}
          {(() => {
            const iS = { width:"100%", padding:"8px 6px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:8, color:"#a5b3c2", fontFamily:"Inter, sans-serif", fontSize:"0.875rem", fontWeight:700, textAlign:"center", outline:"none", boxSizing:"border-box" }
            return (
              <div style={{ padding:"14px 0", borderBottom:"1px solid #1a2a3a" }}>
                <div style={{ fontSize:"0.65rem", fontWeight:800, color:"#4b5563", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>
                  Ganancia
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                  <div>
                    <div style={S.lbl}>% Margen</div>
                    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                      <input type="number" min="0" max="300" style={iS}
                        value={Math.round(c.margen)}
                        onChange={e => set("margen", Math.max(0, Number(e.target.value) || 0))} />
                      <span style={{ color:"#6b7280", fontSize:"0.8rem", flexShrink:0 }}>%</span>
                    </div>
                  </div>
                  <div>
                    <div style={S.lbl}>Total USD</div>
                    <input type="number" min="0" style={iS}
                      value={ganTotalDraft !== null ? ganTotalDraft : Math.round(costos.ganancia)}
                      onFocus={() => setGanTotalDraft(Math.round(costos.ganancia))}
                      onChange={e => setGanTotalDraft(e.target.value)}
                      onBlur={e => {
                        if (costos.subtotal > 0) set("margen", Math.max(0, (Number(e.target.value) / costos.subtotal) * 100))
                        setGanTotalDraft(null)
                      }} />
                  </div>
                  <div>
                    <div style={S.lbl}>Por persona</div>
                    <input type="number" min="0" style={iS}
                      value={ganPPDraft !== null ? ganPPDraft : (totalPersonas > 0 ? Math.round(costos.ganancia / totalPersonas) : 0)}
                      onFocus={() => setGanPPDraft(totalPersonas > 0 ? Math.round(costos.ganancia / totalPersonas) : 0)}
                      onChange={e => setGanPPDraft(e.target.value)}
                      onBlur={e => {
                        const totalGan = Number(e.target.value) * totalPersonas
                        if (costos.subtotal > 0) set("margen", Math.max(0, (totalGan / costos.subtotal) * 100))
                        setGanPPDraft(null)
                      }} />
                  </div>
                </div>
              </div>
            )
          })()}

                    {/* Tarjetas de variantes */}
          <div style={{ marginTop:16 }}>
            {variantes.length > 1 && (
              <div style={{ fontSize:"0.7rem", color:"#4b5563", fontWeight:600, marginBottom:12 }}>
                {variantes.length} variantes · el hotel de cada opción se suma al subtotal base de forma independiente
              </div>
            )}

            {variantes.map((v, i) => (
              <div key={i} style={{
                background:   i === 0 ? "#091428" : "#0f0f0f",
                border:       `1.5px solid ${i === 0 ? "#1e3a5f" : "#2a2a2a"}`,
                borderRadius: 12,
                padding:      "14px 16px",
                marginBottom: i < variantes.length - 1 ? 10 : 0,
              }}>

                {/* Cabecera: opción + hotel(s) + costo hotel */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:"0.58rem", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em", color: i === 0 ? "#3b82f6" : "#4b5563", marginBottom:5 }}>
                      Opción {i + 1}
                    </div>
                    {c.numDestinos === 1 ? (
                      <div style={{ fontSize:"0.95rem", fontWeight:700, color:"#e5e7eb" }}>
                        {v.hotel1.nombre || <span style={{ color:"#4b5563" }}>(sin nombre)</span>}
                      </div>
                    ) : (
                      <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                        <div style={{ fontSize:"0.85rem", fontWeight:700, color:"#e5e7eb" }}>
                          <span style={{ color:"#6b7280", fontWeight:400 }}>{c.destino1}: </span>
                          {v.hotel1.nombre || <span style={{ color:"#4b5563" }}>—</span>}
                        </div>
                        <div style={{ fontSize:"0.85rem", fontWeight:700, color:"#e5e7eb" }}>
                          <span style={{ color:"#6b7280", fontWeight:400 }}>{c.destino2}: </span>
                          {v.hotel2?.nombre || <span style={{ color:"#4b5563" }}>—</span>}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0, marginLeft:12 }}>
                    <div style={{ fontSize:"0.6rem", color:"#4b5563", marginBottom:3 }}>hotel</div>
                    <div style={{ fontSize:"1rem", fontWeight:800, color:"#c9a84c" }}>
                      USD {Math.round(v.costos.costoHoteles).toLocaleString("es-AR")}
                    </div>
                  </div>
                </div>

                {/* Precio final + por persona */}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:10, borderTop:`1px solid ${i === 0 ? "#1a2a3a" : "#1a1a1a"}` }}>
                  <div>
                    <div style={{ fontSize:"0.6rem", color:"#4b5563", marginBottom:4 }}>Precio final</div>
                    <div style={{ fontSize:"1.7rem", fontWeight:900, color:"#10b981", lineHeight:1, letterSpacing:"-0.02em" }}>
                      USD {v.costos.precioFinal.toLocaleString("es-AR")}
                    </div>
                  </div>
                  {totalPersonas > 0 && v.costos.precioFinal > 0 && (
                    <div style={{ padding:"10px 14px", background:"#0f2340", border:"1px solid #1e3a5f", borderRadius:10, textAlign:"right" }}>
                      <div style={{ fontSize:"0.6rem", color:"#4b5563", marginBottom:4 }}>Por persona</div>
                      <div style={{ fontSize:"1.2rem", fontWeight:900, color:"#93c5fd", lineHeight:1 }}>
                        USD {Math.round(v.costos.precioFinal / totalPersonas).toLocaleString("es-AR")}
                      </div>
                    </div>
                  )}
                </div>

                <button onClick={() => abrirEnGenerador(v)} style={{
                  width:"100%", marginTop:12, padding:"12px 0", borderRadius:8, border:"none",
                  background:"#c9a84c", color:"#0a0a0a", fontFamily:"Inter, sans-serif",
                  fontWeight:800, fontSize:"0.875rem", cursor:"pointer", letterSpacing:"0.02em",
                }}>
                  ✨ Generar propuesta{variantes.length > 1 ? ` — Opción ${i + 1}` : ""}
                </button>

              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}